import React, { useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowDropdown, ArrowUpIcon, DeleteIcon } from "../../../../../../assets/icons";
import { isDashboardFilterApplied } from "../../../../../../common/FiltersFormEngine/querySelectors";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { DashboardFilterInstance, FilterDefinition } from "../../../../../../context/FiltersProvider/types";

type Props = {
	instance: DashboardFilterInstance;
	filter: FilterDefinition;
	onToggle: () => void;
	disabled?: boolean;

	onUpdate: (instanceId: string, patch: Partial<DashboardFilterInstance>) => void;
	onRemove: (instanceId: string) => void;
};

export const DashboardFilterChip = ({ instance, filter, onToggle, disabled, onUpdate, onRemove }: Props) => {
	const { t } = useTranslation();
	const { appliedFilters } = useFiltersRegistry();

	const theme = useTheme();

	const [expanded, setExpanded] = useState(false);

	const handleOpen = (e: React.MouseEvent) => {
		e.stopPropagation();
		onToggle();
	};

	const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onRemove(instance.id);
	};

	const values = Array.isArray(instance.value) ? instance.value : instance.value ? [instance.value] : [];

	const visibleValues = expanded ? values : values.slice(0, 2);
	const hiddenCount = values.length - visibleValues.length;

	const appliedFiltersPayload = appliedFilters.payload;
	const isApplied = isDashboardFilterApplied(instance, appliedFiltersPayload);

	return (
		<Box
			onClick={handleOpen}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2,
				px: 1,
				py: 0.5,
				cursor: "pointer",
				gap: 1,
				maxWidth: "100%",
			}}
		>
			{/* arrows */}
			{instance.open ? <ArrowUpIcon /> : <ArrowDropdown />}

			{/* name */}
			<Typography whiteSpace={"nowrap"} variant="caption" color="primary" fontWeight={700}>
				{filter.name}
			</Typography>

			{/* operator */}
			{instance.operator && (
				<Typography variant="caption" fontWeight={700} color="primary">
					{instance.operator}
				</Typography>
			)}

			{/* value */}
			<Stack direction="row" flexWrap="wrap" gap={0.5} maxWidth="100%" maxHeight={80} overflow="auto">
				<Stack direction="row" flexWrap="wrap" gap={0.5} maxWidth="100%" alignItems="center">
					{visibleValues.map((val) => {
						const label = filter.field?.options?.find((o) => o.id === val)?.label ?? String(val);

						return (
							<Chip
								key={val}
								size="small"
								label={label}
								variant={isApplied ? "filled" : "outlined"}
								color="primary"
								sx={{
									fontSize: theme.typography.caption.fontSize,
									fontWeight: 700,
								}}
								onDelete={() => {
									const next = values.filter((v) => v !== val);

									if (!next.length) {
										onRemove(instance.id);
										return;
									}

									onUpdate(instance.id, {
										value: next,
									});
								}}
							/>
						);
					})}

					{/* collapse / expand */}
					{values.length > 2 && (
						<Chip
							size="small"
							variant={isApplied ? "filled" : "outlined"}
							label={expanded ? "Show less" : `+${hiddenCount}`}
							color="primary"
							onClick={(e) => {
								e.stopPropagation();
								setExpanded((prev) => !prev);
							}}
							sx={{
								fontWeight: 600,
								cursor: "pointer",
							}}
						/>
					)}
				</Stack>

				{/* remove */}
				<Tooltip title={`${t("delete")} ${filter.name}`}>
					<Box
						component="span"
						role="button"
						tabIndex={0}
						onClick={handleRemove}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								onRemove(instance.id);
							}
						}}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 24,
							height: 24,
							borderRadius: "50%",
							boxShadow: 1,
							bgcolor: theme.palette.background.elevation0,
						}}
					>
						<DeleteIcon fontSize="inherit" sx={{ width: 16, height: 16 }} />
					</Box>
				</Tooltip>
			</Stack>
		</Box>
	);
};
